<div class="meeting-details-card">
  <div class="card-header">
    @if (title !== null) {
      <div class="header-content">
        <h3>
          @if (stepNumber !== null) {
            {{ 'CALL_SETUP.STEP_LABEL' | translate: { number: stepNumber } }}
          }
          {{ title }}
        </h3>
        @if (description !== null) {
          <p>{{ description }}</p>
        }
      </div>
    }
    <div class="header-actions">
      @if (canEdit) {
        @if (isEditingDetails) {
          <button mat-button class="discard-button" (click)="cancelEditDetails()">
            {{ 'COMMON.DISCARD' | translate }}
          </button>
          <button mat-button class="done-button" (click)="saveDetails()" [disabled]="!isFormValid">
            <mat-icon>check</mat-icon>
            {{ 'COMMON.DONE' | translate }}
          </button>
        } @else {
          <button mat-button class="edit-button" (click)="startEditDetails()">
            <mat-icon>edit</mat-icon>
            {{ 'COMMON.EDIT' | translate }}
          </button>
        }
      }
    </div>
  </div>

  @if (isEditingDetails) {
    <!-- Edit Mode -->
    <div class="details-form">
      <!-- Date/Time and Duration Row -->
      <div class="form-row">
        <app-datetime-picker
          [(ngModel)]="editedDetailsFormData.date"
          [required]="false"
          [disabled]="!canEditDateTime"
          [isDateInvalid]="isDateTooFarInPast(editedDetailsFormData.date)"
          errorMessageKey="VALIDATION.DATE_TOO_FAR_IN_PAST"
          translationKey="CALL_SETUP.CALL_DETAILS.CALL_DATE"
        ></app-datetime-picker>
        @if (!isUploadedCall) {
          <mat-form-field appearance="outline" class="duration-field" [class.disabled]="!canEditDateTime">
            <mat-label>{{ 'COMMON.DURATION' | translate }}</mat-label>
            <div class="duration-control">
              <button
                mat-icon-button
                mat-small-icon-button
                type="button"
                (click)="$event.stopPropagation(); decreaseDuration()"
                [disabled]="decreaseDurationButtonDisabled"
              >
                <mat-icon>remove</mat-icon>
              </button>
              <input matInput readonly [value]="editedDetailsFormData.duration + ' ' + ('COMMON.MINUTE_ABBREVIATION' | translate)" />
              <button
                mat-icon-button
                mat-small-icon-button
                type="button"
                (click)="$event.stopPropagation(); increaseDuration()"
                [disabled]="increaseDurationButtonDisabled"
              >
                <mat-icon>add</mat-icon>
              </button>
            </div>
          </mat-form-field>
        }
      </div>

      <!-- Meeting Type, Location & Company Row -->
      <div class="form-row three-columns">
        <app-select
          [(ngModel)]="editedDetailsFormData.governingBodyType"
          labelKey="CALL_SETUP.CALL_DETAILS.CALL_TYPE"
          nullValueLabelKey="CALL_SETUP.CALL_DETAILS.NOT_SET"
          [options]="governingBodyTypeOptions"
        ></app-select>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'CALL_SETUP.CALL_DETAILS.LOCATION' | translate }}</mat-label>
          <input matInput [(ngModel)]="editedDetailsFormData.location" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'CALL_SETUP.CALL_DETAILS.COMPANY' | translate }}</mat-label>
          <input matInput [(ngModel)]="editedDetailsFormData.companyName" />
        </mat-form-field>
      </div>

      <!-- Committee Type (only meaningful for the COMMITTEE governing body type) -->
      @if (editedDetailsFormData.governingBodyType === 'COMMITTEE') {
        <div class="form-row">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ 'CALL_SETUP.CALL_DETAILS.COMMITTEE_TYPE' | translate }}</mat-label>
            <input
              matInput
              [(ngModel)]="editedDetailsFormData.committeeType"
              [placeholder]="'CALL_SETUP.CALL_DETAILS.COMMITTEE_TYPE_PLACEHOLDER' | translate"
            />
          </mat-form-field>
        </div>
      }

      <!-- Video Conferencing URL -->
      @if (!isUploadedCall) {
        <div class="form-row">
          <mat-form-field appearance="outline" class="full-width" [class.disabled]="!canEditLink">
            <mat-label>{{ 'CALL_SETUP.CALL_DETAILS.VIDEO_CONFERENCING_URL' | translate }}</mat-label>
            <input matInput [(ngModel)]="editedDetailsFormData.link" type="url" [required]="canEditLink" [disabled]="!canEditLink" />
          </mat-form-field>
        </div>
      }

      <!-- Entities -->
      <div class="form-section">
        <label class="field-label">{{ 'CALL_SETUP.CALL_DETAILS.ENTITIES' | translate }}</label>
        @if (editedDetailsFormData.entities.length > 0) {
          <div class="entities-chips">
            @for (entity of editedDetailsFormData.entities; track entity) {
              <mat-chip (removed)="removeEntity(entity)">
                {{ entity }}
                <button matChipRemove>
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip>
            }
          </div>
        }
        <div class="add-entity-row">
          <mat-form-field appearance="outline" class="entity-input">
            <mat-label>{{ 'CALL_SETUP.CALL_DETAILS.ADD_ENTITY_PLACEHOLDER' | translate }}</mat-label>
            <input matInput [(ngModel)]="editedDetailsFormData.newEntity" (keyup.enter)="addEntity()" />
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="addEntity()" [disabled]="!editedDetailsFormData.newEntity.trim()">
            {{ 'COMMON.ADD' | translate }}
          </button>
        </div>
      </div>
    </div>
  } @else {
    <!-- View Mode -->
    <div class="details-view">
      <!-- Date & Time -->
      <div class="view-row">
        <div class="view-item">
          <div class="view-label">
            <mat-icon>calendar_today</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.DATE' | translate }}
          </div>
          <div class="view-value">{{ call.date | date: 'EEEE, d MMMM y' }}</div>
        </div>
        <div class="view-item">
          <div class="view-label">
            <mat-icon>schedule</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.TIME' | translate }}
          </div>
          <div class="view-value">
            @if (isUploadedCall) {
              {{ call.date | date: 'HH:mm' }}
            } @else {
              {{ call.date | date: 'HH:mm' }} - {{ call.endDate | date: 'HH:mm' }}
            }
          </div>
        </div>
        @if (call.provider !== null) {
          <div class="view-item">
            <div class="view-label">
              <mat-icon>videocam</mat-icon>
              {{ 'CALL_SETUP.CALL_DETAILS.PROVIDER_TYPE' | translate }}
            </div>
            <div class="view-value">
              {{ 'CALL_SETUP.CALL_DETAILS.PROVIDER_TYPES.' + call.provider | translate }}
            </div>
          </div>
        }
      </div>

      <!-- Meeting Type, Location, Company -->
      <div class="view-row">
        <div class="view-item">
          <div class="view-label">
            <mat-icon>groups</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.CALL_TYPE' | translate }}
          </div>
          <div class="view-value" [class.not-set]="metadata.governingBodyType === null">
            @if (metadata.governingBodyType !== null) {
              {{ 'CALL_SETUP.CALL_DETAILS.GOVERNING_BODY_TYPE.' + metadata.governingBodyType | translate }}
            } @else {
              {{ 'CALL_SETUP.CALL_DETAILS.NOT_SET' | translate }}
            }
          </div>
        </div>
        @if (metadata.governingBodyType === 'COMMITTEE') {
          <div class="view-item">
            <div class="view-label">
              <mat-icon>groups</mat-icon>
              {{ 'CALL_SETUP.CALL_DETAILS.COMMITTEE_TYPE' | translate }}
            </div>
            <div class="view-value" [class.not-set]="metadata.committeeType === null">
              {{ metadata.committeeType ?? ('CALL_SETUP.CALL_DETAILS.NOT_SET' | translate) }}
            </div>
          </div>
        }
        <div class="view-item">
          <div class="view-label">
            <mat-icon>place</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.LOCATION' | translate }}
          </div>
          <div class="view-value" [class.not-set]="metadata.location === null">
            {{ metadata.location ?? ('CALL_SETUP.CALL_DETAILS.NOT_SET' | translate) }}
          </div>
        </div>
        <div class="view-item">
          <div class="view-label">
            <mat-icon>business</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.COMPANY' | translate }}
          </div>
          <div class="view-value" [class.not-set]="metadata.companyName === null">
            {{ metadata.companyName ?? ('CALL_SETUP.CALL_DETAILS.NOT_SET' | translate) }}
          </div>
        </div>
      </div>

      <!-- Video Conferencing URL -->
      @if (call.link !== null) {
        <div class="view-section">
          <div class="view-label">
            <mat-icon>link</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.VIDEO_CONFERENCING_URL' | translate }}
          </div>
          <div class="view-value link-added" [matTooltip]="call.link" matTooltipPosition="below" matTooltipShowDelay="0">
            <mat-icon>check</mat-icon>
            {{ 'CALL_SETUP.CALL_DETAILS.LINK_ADDED' | translate }}
            <mat-icon class="copy-link-icon" (click)="copyLink()">content_copy</mat-icon>
          </div>
        </div>
      }

      <!-- Entities -->
      <div class="view-section">
        <div class="view-label">
          <mat-icon>corporate_fare</mat-icon>
          {{ 'CALL_SETUP.CALL_DETAILS.ENTITIES' | translate }}
        </div>
        <div class="view-value" [class.not-set]="metadata.entities.length === 0">
          @if (metadata.entities.length > 0) {
            <div class="entities-chips view-chips">
              @for (entity of metadata.entities; track entity) {
                <mat-chip>{{ entity }}</mat-chip>
              }
            </div>
          } @else {
            {{ 'CALL_SETUP.CALL_DETAILS.NONE' | translate }}
          }
        </div>
      </div>
    </div>
  }
</div>
